import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AckWaitingService, IOcbAckWaiting, UserService } from '@app/core/';
import { TranslateService } from '@ngx-translate/core';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-home-ack-waiting-list',
    templateUrl: './home-ack-waiting-list.component.html',
    styleUrls: ['./home-ack-waiting-list.component.scss'],
})
export class HomeAckWaitingListComponent implements AfterViewInit {

    // Main data source (ack waiting)
    public ackWaitingList: IOcbAckWaiting[];
    public ackWaitingTree: AckWaiting[] = new Array<AckWaiting>();

    public selectedAck: AckWaiting[] = new Array<AckWaiting>();

    // Tables to filter environments rights
    public environmentsAccess: boolean[] = new Array<boolean>();
    public isDisabled: boolean = true;

    /**
     * Boolean to show or hide the loading panel
     */
    public isLoadPanelVisible: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private translateService: TranslateService,
        private ackWaitingService: AckWaitingService,
    ) {

    }

    public ngAfterViewInit(): void {
        this.userService.user.subscribe(admin => {
            admin.environments.forEach(env => {
                this.environmentsAccess[env.environmentId] = env.canDisableACKAlerts;
            });
        });
        this.loadDataSource();
    }

    public loadDataSource() {
        this.ackWaitingTree = new Array<AckWaiting>();
        this.ackWaitingService.getAckWaiting(undefined, undefined, false).subscribe(
            ackWaiting => {
                this.ackWaitingList = ackWaiting;
                const environments = new Array<any>();

                this.ackWaitingList.map(ack => {
                    return { environmentId: ack.environmentId, environmentName: ack.environmentName };
                }).forEach(env => {
                    if (environments.indexOf(env.environmentId) === -1) {
                        environments.push(env.environmentId);
                        this.ackWaitingTree.push({ id: env.environmentId, text: env.environmentName, expanded: true });
                    }
                });

                let currentLocale = 'en-US';
                switch (this.userService.language) {
                    case 'en': currentLocale = 'en-US'; break;
                    case 'fr': currentLocale = 'fr-FR'; break;
                }

                this.ackWaitingList.forEach(ack => {
                    const dateString = new Date(ack.fileSentOn + 'Z').toLocaleString(currentLocale);
                    const displaySubText = dateString + ' - ' + ack.fileTransferName;
                    this.ackWaitingTree.push({ id: ack.id, text: ack.sentFileName, subText: displaySubText, parentId: ack.environmentId });
                });
            },
            err => {
                this.handleError(err);
            });
    }

    public disableAlert(): void {
        forkJoin(
            // Get translation messages
            this.translateService.get('ackWaiting.msgDisableConfirm'),
            this.translateService.get('ackWaiting.msgDisableConfirmTitle'))
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the requests
                mergeMap(_ =>
                    forkJoin(this.selectedAck.map(ack => this.ackWaitingService.disableAlert(ack.id))),
                ),
                // Display confirm message
                mergeMap(_ => this.translateService.get('ackWaiting.msgDisableComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe(
                message => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err));
    }

    /**
     * Event catching selection
     * @param e Handle event
     */
    public selectionChanged(e) {

        if (e.node.selected) {
            this.selectedAck.push(e.itemData);
        } else {
            this.selectedAck = this.selectedAck.filter(ack => ack.id !== e.itemData.id);
        }

        if (this.selectedAck.length === 0) { this.isDisabled = true; }
        else { this.isDisabled = false; }

        this.selectedAck.forEach(ack => {
            if (!this.environmentsAccess[ack.parentId]) {
                this.isDisabled = true;
            }
        });
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}

interface AckWaiting {
    id?: string;
    text?: string;
    subText?: string;
    parentId?: string;
    expanded?: boolean;
}
